import { useState, useEffect } from 'react';

interface MockAnalysisResult {
  score: number;
  category: string;
  insights: string[];
  personalizedBenefits: string[];
  urgencyMessage: string;
  ctaText: string;
  recommendations?: unknown[];
  analysis?: string; // For assessments with custom result pages
}

export function useMockAssessmentAnalysis(resultsType: 'generic' | 'ai', assessmentType?: string) {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<MockAnalysisResult | null>(null);

  useEffect(() => {
    // Simulate analyzing delay
    const analyzeTimer = setTimeout(() => {
      setIsAnalyzing(false);
      
      // Simulate completion delay
      setTimeout(() => {
        // Special handling for personalized-goal-plan
        if (assessmentType === 'personalized-goal-plan') {
          const mockResult: MockAnalysisResult = {
            score: 85,
            category: 'Growth-Oriented Achiever',
            insights: [
              'You have a clear vision for personal growth but need structured support to achieve it',
              'Your main challenge is balancing multiple priorities while maintaining focus on your primary goal',
              'You show high motivation when working towards meaningful objectives with clear milestones'
            ],
            personalizedBenefits: [
              'AI-powered goal planning tailored to your unique situation and aspirations',
              'Weekly milestone tracking with adaptive coaching based on your progress',
              'Personalized accountability system that aligns with your motivation style',
              'Strategic time management techniques for your specific challenges'
            ],
            urgencyMessage: 'Based on your responses, implementing a structured goal achievement system could help you reach your objectives 3x faster.',
            ctaText: 'Get Your Personalized Goal Plan',
            analysis: `
              <h2>Primary Goal</h2>
              <p>Launch and scale your online coaching business to $10K monthly revenue within 6 months</p>
              
              <h2>Focus Area</h2>
              <p>Business Development & Personal Branding</p>
              
              <h2>Timeline</h2>
              <p>6-month strategic journey with bi-weekly milestones</p>
              
              <h2>Sub-Goals</h2>
              <ul>
                <li>- Define your unique coaching niche and ideal client avatar</li>
                <li>- Create compelling service packages and pricing structure</li>
                <li>- Build a professional online presence and content strategy</li>
                <li>- Develop lead generation and client acquisition systems</li>
                <li>- Establish sustainable business operations and workflows</li>
              </ul>
              
              <h2>Challenges</h2>
              <ul>
                <li>- Limited time due to current full-time commitments</li>
                <li>- Uncertainty about marketing and client acquisition</li>
                <li>- Need for structured accountability and guidance</li>
              </ul>
              
              <h2>Next Steps</h2>
              <ul>
                <li>- Complete market research on your coaching niche</li>
                <li>- Draft your unique value proposition and service offerings</li>
                <li>- Set up basic online presence (website/social media)</li>
                <li>- Create your first lead magnet or free resource</li>
              </ul>
            `
          };
          
          setAnalysisResult(mockResult);
          setIsComplete(true);
          return;
        }
        
        // Default mock results for other assessment types
        const mockResult: MockAnalysisResult = resultsType === 'ai' ? {
          score: 78,
          category: 'Strategic Procrastinator',
          insights: [
            'You tend to delay tasks that lack clear immediate rewards or personal meaning',
            'Your procrastination peaks when facing complex, multi-step projects without defined milestones',
            'You perform best under moderate pressure with well-defined deadlines and accountability'
          ],
          personalizedBenefits: [
            'AI-powered task breakdown to make complex projects manageable',
            'Personalized accountability system based on your motivation patterns',
            'Custom reward mechanisms aligned with your value system',
            'Strategic deadline setting that leverages your pressure-performance sweet spot'
          ],
          urgencyMessage: 'Based on your responses, implementing a structured system could save you 10-15 hours per week currently lost to procrastination cycles.',
          ctaText: 'Start Your Personalized Anti-Procrastination Plan',
          recommendations: []
        } : {
          score: 65,
          category: 'Time Management Challenger',
          insights: [
            'You often struggle with prioritizing tasks effectively',
            'Time estimation for projects tends to be overly optimistic',
            'You work better with structured schedules and clear boundaries'
          ],
          personalizedBenefits: [
            'Proven time-blocking techniques to improve focus',
            'Priority matrix templates for better task management',
            'Weekly planning system to stay on track',
            'Productivity tracking to measure improvement'
          ],
          urgencyMessage: 'Take control of your time and boost your productivity with our proven system.',
          ctaText: 'Get Your Time Management Toolkit',
          recommendations: []
        };
        
        setAnalysisResult(mockResult);
        setIsComplete(true);
      }, 2000);
    }, 3000);

    return () => {
      clearTimeout(analyzeTimer);
    };
  }, [resultsType, assessmentType]);

  return {
    isAnalyzing,
    isComplete,
    analysisResult
  };
}